import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Typography, Paper, TextField, MenuItem, Button, Chip,
    Alert, CircularProgress, Divider, Checkbox, ListItemText, IconButton,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import REVIEWABLE_MODULES, { moduleByKey, REVIEWABLE_META_FIELDS, ADVISORY_OPTIONS, backendUrl } from "./reviewableModules";
import "../IncidentReports/DailySituationReport.css";

const authHeaders = () => {
    const token = localStorage.getItem("access_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
};
const authHeadersJson = () => ({ "Content-Type": "application/json", ...authHeaders() });

const ALL_MODULE_KEYS = REVIEWABLE_MODULES.map((m) => m.key);

const STATUS_TABS = [
    { id: "pending", label: "Pending" },
    { id: "verified", label: "Verified" },
    { id: "rejected", label: "Rejected" },
    { id: "all", label: "All" },
];

const STATUS_COLORS = {
    pending: { bg: "#a8791c18", fg: "#a8791c" },
    verified: { bg: "#f0fdf4", fg: "#3d7a4c" },
    rejected: { bg: "#fdf2f2", fg: "#ad4141" },
    merged: { bg: "#faf5ff", fg: "#7c3aed" },
};

// Report ids are only unique within a module (Cyclone #5 and Flood #5 are
// different reports) - now that Category can select several modules at
// once, every id used for selection/keys has to carry its module along.
const reportUid = (moduleKey, id) => `${moduleKey}:${id}`;

const humanize = (key) => key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Felt-report forms save municipality/sub_municipality/suco as ADM codes
// (e.g. "TL03", "TL0301", "TL030101"), not names - same as every other
// consumer of these tables (common/views.py's ActiveEventsView does the
// same code->name lookup server-side). Resolved here from the master
// Municipality/SubMunicipality/Suco tables instead of showing raw codes.
// `mod.locationFields` is always [municipality, sub-municipality, suco] in
// that order, just under a differing field name per module (e.g.
// roadaccident's "submunicipality" has no underscore).
const resolveLocationParts = (mod, report, locationNames) => {
    const [muniField, subField, sucoField] = mod.locationFields;
    return {
        municipality: locationNames.municipality.get(report[muniField]) || report[muniField] || "",
        subMunicipality: locationNames.subMunicipality.get(report[subField]) || report[subField] || "",
        suco: locationNames.suco.get(report[sucoField]) || report[sucoField] || "",
    };
};

// The one set of fields every hazard module's felt-report model carries
// identically for loss/damage estimates (see each model's "Loss/damage
// estimates, reported by the submitter" fields) - Black Spot names its two
// equivalents differently, everything else shares the same 5 field names.
// Shown as its own read-only section, separate from the generic hazard
// details dump below.
const LOSS_DAMAGE_FIELDS = [
    { key: "people_deaths_count", label: "Deaths" },
    { key: "numberoffatal", label: "Deaths" },
    { key: "people_injured_count", label: "People Injured" },
    { key: "numberofinjured", label: "People Injured" },
    { key: "houses_affected_count", label: "Houses Affected" },
    { key: "livestock_lost_count", label: "Livestock Lost" },
    { key: "estimated_loss_amount", label: "Estimated Loss Amount" },
];
const LOSS_DAMAGE_FIELD_KEYS = new Set(LOSS_DAMAGE_FIELDS.map((f) => f.key));

// Only the loss/damage fields this report's model actually has, with a
// value already on the report (read-only display use).
const lossDamageDetails = (report) =>
    LOSS_DAMAGE_FIELDS
        .filter((f) => f.key in report && report[f.key] !== null && report[f.key] !== "")
        .map((f) => ({ ...f, value: String(report[f.key]) }));

// Everything on a report that isn't location/date/description/audit
// metadata or a loss/damage field (shown in its own section above) - shown
// as a plain label:value dump since each of the nine modules has its own
// hazard-specific columns.
const otherDetails = (report, mod) => {
    const skip = new Set([...REVIEWABLE_META_FIELDS, ...mod.locationFields, mod.dateField, mod.descField, "location", "latitude", "longitude", "geojson_feature", "starting_point_lat", "starting_point_long", "ending_point_lat", "ending_point_long", "advisory", "_moduleKey", ...LOSS_DAMAGE_FIELD_KEYS]);
    return Object.entries(report)
        .filter(([key, value]) => !skip.has(key) && value !== null && value !== "" && typeof value !== "object")
        .map(([key, value]) => ({ key, label: humanize(key), value: String(value) }));
};

// Full detail for one duplicate, shown inline under its row in "Merged into
// this report" when its expand arrow is toggled on - same shape of
// information the main selected report gets above (location, reporter,
// description, photo, hazard-specific fields), just condensed. Its own
// component (not inlined in the list) because the photo needs its own
// auth'd blob fetch, same reasoning as the main report's image effect.
const MergedMemberDetail = ({ report, mod, locationNames }) => {
    const [imageBlobUrl, setImageBlobUrl] = useState(null);
    const loc = resolveLocationParts(mod, report, locationNames);

    useEffect(() => {
        if (!report.image_url) { setImageBlobUrl(null); return; }
        let objectUrl = null;
        let cancelled = false;
        (async () => {
            try {
                const response = await fetch(`${backendUrl}${report.image_url}`, { headers: authHeaders() });
                if (!response.ok) throw new Error("Failed to load photo");
                const blob = await response.blob();
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setImageBlobUrl(objectUrl);
            } catch (error) {
                console.error("Error loading merged report photo:", error);
                if (!cancelled) setImageBlobUrl(null);
            }
        })();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [report.image_url]);

    const details = [...otherDetails(report, mod), ...lossDamageDetails(report)];

    return (
        <Box sx={{ pl: 1, pr: 1, py: 1.5, mb: 1, backgroundColor: "#f8fafc", borderRadius: "8px" }}>
            <Typography sx={{ fontSize: "12.5px", color: "#64748b", mb: 1 }}>
                {[loc.municipality, loc.subMunicipality, loc.suco].filter(Boolean).join(", ")}
                {report[mod.dateField] ? ` · ${report[mod.dateField]}` : ""}
                {report.submitted_by_detail?.full_name ? ` · reported by ${report.submitted_by_detail.full_name}` : ""}
            </Typography>
            {mod.descField && report[mod.descField] && (
                <Typography sx={{ fontSize: "13px", mb: 1 }}>{report[mod.descField]}</Typography>
            )}
            {report.advisory && (
                <Chip
                    label={`Advisory: ${report.advisory}`} size="small"
                    sx={{ mb: 1, backgroundColor: "#eef2ff", color: "#4338ca", fontWeight: 700 }}
                />
            )}
            {report.image_url && (
                <Box sx={{ mb: 1 }}>
                    {imageBlobUrl ? (
                        <a href={imageBlobUrl} target="_blank" rel="noreferrer">
                            <img
                                src={imageBlobUrl}
                                alt="Attachment for this duplicate"
                                style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8, border: "1px solid #e2e8f0" }}
                            />
                        </a>
                    ) : (
                        <Typography sx={{ fontSize: "12px", color: "#94a3b8" }}>Loading photo…</Typography>
                    )}
                </Box>
            )}
            {details.length > 0 && (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "4px 16px" }}>
                    {details.map((d) => (
                        <Typography key={d.label} sx={{ fontSize: "12px", color: "#374151" }}>
                            <span style={{ color: "#94a3b8" }}>{d.label}:</span> {d.value}
                        </Typography>
                    ))}
                </Box>
            )}
        </Box>
    );
};

const ReportApprovals = () => {
    // Multi-select, all categories on by default.
    const [moduleKeys, setModuleKeys] = useState(ALL_MODULE_KEYS);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUid, setSelectedUid] = useState(null);
    const [selected, setSelected] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [busy, setBusy] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    // Advisory is decided by the reviewer at verification time, not by
    // whoever submitted the report - optional, only asked for right before
    // clicking Verify.
    const [advisoryChoice, setAdvisoryChoice] = useState("");
    const [imageBlobUrl, setImageBlobUrl] = useState(null);
    const [locationNames, setLocationNames] = useState({
        municipality: new Map(), subMunicipality: new Map(), suco: new Map(),
    });
    // Which merged-duplicate rows currently have their full info expanded,
    // and the fetched detail behind each - independent per row, so opening
    // one doesn't affect any other; clicking the arrow again on that same
    // row collapses just that one.
    const [expandedMemberIds, setExpandedMemberIds] = useState(new Set());
    const [memberDetails, setMemberDetails] = useState({});

    // Master-table code->name lookups (Municipality/SubMunicipality/Suco),
    // fetched once, unfiltered, the same endpoints the DSR bulk template
    // screen already uses - so every report's location can be resolved to
    // a name locally instead of showing raw ADM codes.
    useEffect(() => {
        (async () => {
            try {
                const [muniRes, subRes, sucoRes] = await Promise.all([
                    fetch(`${backendUrl}/Municipalities/municipalities_list/`, { method: "POST", headers: authHeaders() }),
                    fetch(`${backendUrl}/Municipalities/sub_municipalities_list/`, { method: "POST", headers: authHeaders() }),
                    fetch(`${backendUrl}/Municipalities/suco_master_list/`, { headers: authHeaders() }),
                ]);
                const [munis, subs, sucos] = await Promise.all([muniRes.json(), subRes.json(), sucoRes.json()]);
                setLocationNames({
                    municipality: new Map((Array.isArray(munis) ? munis : []).map((m) => [m.adm1_code, m.adm1])),
                    subMunicipality: new Map((Array.isArray(subs) ? subs : []).map((s) => [s.adm2_code, s.adm2])),
                    suco: new Map((Array.isArray(sucos) ? sucos : []).map((s) => [s.adm3_code, s.adm3])),
                });
            } catch (error) {
                console.error("Error loading municipality/suco master lists:", error);
            }
        })();
    }, []);

    const selectedModules = useMemo(
        () => REVIEWABLE_MODULES.filter((m) => moduleKeys.includes(m.key)),
        [moduleKeys]
    );
    // The detail panel/handlers only ever act on one report at a time, so
    // they resolve their own module from whichever report is open - Category
    // being multi-select only affects the list/fetch side.
    const selectedMod = selected ? moduleByKey(selected._moduleKey) : null;
    const selectedLoc = selected && selectedMod ? resolveLocationParts(selectedMod, selected, locationNames) : null;

    const clearSelection = () => {
        setSelectedUid(null); setSelected(null); setCandidates([]); setAdvisoryChoice("");
        setExpandedMemberIds(new Set()); setMemberDetails({});
    };

    // Toggles a duplicate's inline full-detail view. Fetches its full
    // detail (same endpoint the main report uses) the first time it's
    // expanded; cached afterward so re-toggling doesn't re-fetch.
    const toggleMemberExpand = async (id) => {
        setExpandedMemberIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
        if (!memberDetails[id] && selectedMod) {
            try {
                const response = await fetch(`${selectedMod.base}${id}/`, { headers: authHeaders() });
                const data = await response.json();
                if (response.ok) setMemberDetails((prev) => ({ ...prev, [id]: data }));
            } catch (error) {
                console.error("Error loading merged report detail:", error);
            }
        }
    };

    // Photos are served from an authenticated endpoint (BLOB in the DB, not
    // a static file), so a plain <img src> can't carry the Authorization
    // header - fetch it as a blob instead and point the <img> at that.
    useEffect(() => {
        if (!selected?.image_url) { setImageBlobUrl(null); return; }
        let objectUrl = null;
        let cancelled = false;
        (async () => {
            try {
                const response = await fetch(`${backendUrl}${selected.image_url}`, { headers: authHeaders() });
                if (!response.ok) throw new Error("Failed to load photo");
                const blob = await response.blob();
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setImageBlobUrl(objectUrl);
            } catch (error) {
                console.error("Error loading report photo:", error);
                if (!cancelled) setImageBlobUrl(null);
            }
        })();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [selected?.image_url]);

    // Fetches every selected category's report list in parallel and flattens
    // them into one array, each row tagged with which module it came from
    // (_moduleKey) - one module failing to load doesn't take the rest down
    // with it, it just contributes nothing and gets its own error toast.
    // Fans out to every selected category's endpoint in parallel, so one
    // call can involve up to 9 in-flight requests. Merging/unmerging/
    // promoting each trigger their own fetchReports() call to refresh -
    // network responses aren't guaranteed to resolve in the order they were
    // sent, so without this guard an older call finishing after a newer one
    // (e.g. merging a 2nd duplicate right after the 1st) would silently
    // overwrite the fresh state with stale data - "1 active + 2 merged"
    // reverting back to showing just 1. Only the most recently *started*
    // call is allowed to commit its result.
    const fetchSeqRef = useRef(0);

    const fetchReports = useCallback(async () => {
        const seq = ++fetchSeqRef.current;
        setLoading(true);
        try {
            const results = await Promise.all(
                selectedModules.map(async (m) => {
                    try {
                        const response = await fetch(m.base, { headers: authHeaders() });
                        const data = await response.json();
                        if (!response.ok) throw new Error((data && data.error) || `Failed to load ${m.label} reports`);
                        // Cyclone/Flood/Landslide/Lightning/FireRisk's list endpoint
                        // predates this page and is shared with other consumers, so
                        // it still returns a plain array; Earthquake/Road-family's
                        // newer admin-list endpoints return {count, reports}.
                        const rows = Array.isArray(data) ? data : (data.reports || []);
                        return rows.map((r) => ({ ...r, _moduleKey: m.key }));
                    } catch (error) {
                        console.error(`Error fetching ${m.label} reports:`, error);
                        setSnackbar({ open: true, message: error.message || `Could not load ${m.label} reports.`, severity: "error" });
                        return [];
                    }
                })
            );
            if (fetchSeqRef.current !== seq) return; // a newer fetchReports() call has since superseded this one
            setReports(results.flat());
        } finally {
            if (fetchSeqRef.current === seq) setLoading(false);
        }
    }, [selectedModules]);

    useEffect(() => { clearSelection(); fetchReports(); }, [selectedModules, fetchReports]);

    useEffect(() => {
        if (!snackbar.open) return;
        const timer = setTimeout(() => setSnackbar((p) => ({ ...p, open: false })), 6000);
        return () => clearTimeout(timer);
    }, [snackbar.open]);

    // Reports actually merged into the one currently open - as opposed to
    // `candidates` below, which are unmerged suggestions. Admin/Super Admin
    // sees every status via visible_queryset(), so this list already
    // includes them without a separate fetch. Matched on module too, since
    // ids collide across categories.
    const groupMembers = useMemo(() => {
        if (!selected) return [];
        return reports.filter((r) => r.merged_into === selected.id && r._moduleKey === selected._moduleKey);
    }, [reports, selected]);

    // Latest event date first, across every module at once - not grouped by
    // module (fetchReports concatenates each module's own results in turn,
    // so without this the list would show all of Cyclone, then all of
    // Flood, etc. instead of one true chronological feed). Reports with no
    // parseable date field sort to the end rather than breaking the sort.
    const visibleReports = useMemo(() => {
        const filtered = statusFilter === "all"
            ? reports.filter((r) => r.verification_status !== "merged")
            : reports.filter((r) => r.verification_status === statusFilter);
        const reportTime = (r) => {
            const mod = moduleByKey(r._moduleKey);
            const t = mod ? new Date(r[mod.dateField]).getTime() : NaN;
            return Number.isNaN(t) ? -Infinity : t;
        };
        return [...filtered].sort((a, b) => reportTime(b) - reportTime(a));
    }, [reports, statusFilter]);

    const summary = useMemo(() => ({
        total: reports.length,
        pending: reports.filter((r) => r.verification_status === "pending").length,
        verified: reports.filter((r) => r.verification_status === "verified").length,
        rejected: reports.filter((r) => r.verification_status === "rejected").length,
        merged: reports.filter((r) => r.verification_status === "merged").length,
    }), [reports]);

    const selectReport = async (moduleKey, id) => {
        const mod = moduleByKey(moduleKey);
        if (!mod) return;
        setSelectedUid(reportUid(moduleKey, id));
        setSelected(null);
        setAdvisoryChoice("");
        try {
            const response = await fetch(`${mod.base}${id}/`, { headers: authHeaders() });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || "Failed to load report");
            setSelected({ ...data, _moduleKey: moduleKey });
            setAdvisoryChoice(data.advisory || "");
            const candidateResponse = await fetch(`${mod.base}${id}/duplicates/`, { headers: authHeaders() });
            const candidateData = await candidateResponse.json();
            setCandidates((candidateData.reports || []).map((c) => ({ ...c, _moduleKey: moduleKey })));
        } catch (error) {
            console.error("Error loading report detail:", error);
            setSnackbar({ open: true, message: error.message || "Failed to load report.", severity: "error" });
        }
    };

    const handleReview = async (action) => {
        if (!selected || !selectedMod) return;
        setBusy(true);
        try {
            const body = { action };
            if (action === "verify" && selectedMod.hasAdvisory && advisoryChoice) body.advisory = advisoryChoice;
            const response = await fetch(`${selectedMod.base}${selected.id}/review/`, {
                method: "POST", headers: authHeadersJson(), body: JSON.stringify(body),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.error || `Failed to ${action} report`);
            const verb = { verify: "verified", reject: "rejected", reopen: "reopened for review" }[action];
            setSnackbar({ open: true, message: `${selectedMod.label} report #${selected.id} ${verb}.`, severity: "success" });
            clearSelection();
            fetchReports();
        } catch (error) {
            console.error(`Error ${action}ing report:`, error);
            setSnackbar({ open: true, message: error.message || `Failed to ${action} report.`, severity: "error" });
        } finally {
            setBusy(false);
        }
    };

    // "Merge into this" merges the candidate into the currently open report
    // (selected stays canonical/active) - not the other way round, so
    // clicking it on several candidates in a row keeps piling duplicates
    // onto the same active report instead of the active report jumping to
    // whichever candidate was clicked last. Panel stays open afterward
    // (re-selects the same report) so the reviewer can merge another
    // candidate right away; it only closes on Verify/Reject/Reopen.
    const handleMerge = async (candidateId) => {
        if (!selected || !selectedMod) return;
        setBusy(true);
        try {
            const response = await fetch(`${selectedMod.base}${candidateId}/merge/`, {
                method: "POST", headers: authHeadersJson(), body: JSON.stringify({ merge_into: selected.id }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.error || "Failed to merge report");
            setSnackbar({ open: true, message: `Report #${candidateId} merged into #${selected.id}.`, severity: "success" });
            await Promise.all([selectReport(selected._moduleKey, selected.id), fetchReports()]);
        } catch (error) {
            console.error("Error merging report:", error);
            setSnackbar({ open: true, message: error.message || "Failed to merge report.", severity: "error" });
        } finally {
            setBusy(false);
        }
    };

    const handleUnmerge = async (id) => {
        if (!selectedMod) return;
        setBusy(true);
        try {
            const response = await fetch(`${selectedMod.base}${id}/unmerge/`, { method: "POST", headers: authHeadersJson() });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.error || "Failed to unmerge report");
            setSnackbar({ open: true, message: `Report #${id} unmerged.`, severity: "success" });
            await Promise.all([selected ? selectReport(selected._moduleKey, selected.id) : Promise.resolve(), fetchReports()]);
        } catch (error) {
            console.error("Error unmerging report:", error);
            setSnackbar({ open: true, message: error.message || "Failed to unmerge report.", severity: "error" });
        } finally {
            setBusy(false);
        }
    };

    // Reassigns which report in the group is canonical/"active" - the one
    // clicked becomes active, the current active one (and every other
    // member) folds under it instead. Admin/Super Admin only (enforced
    // server-side); jumps the selection to the newly-active report since
    // the one that was open a moment ago is now the duplicate.
    const handlePromote = async (id) => {
        if (!selectedMod) return;
        const moduleKey = selectedMod.key;
        setBusy(true);
        try {
            const response = await fetch(`${selectedMod.base}${id}/promote/`, { method: "POST", headers: authHeadersJson() });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.error || "Failed to make this report active");
            setSnackbar({ open: true, message: `Report #${id} is now the active report for this incident.`, severity: "success" });
            await Promise.all([selectReport(moduleKey, id), fetchReports()]);
        } catch (error) {
            console.error("Error promoting report:", error);
            setSnackbar({ open: true, message: error.message || "Failed to make this report active.", severity: "error" });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box className="dsr-container">
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 2 }}>
                    <TextField
                        label="Category" select size="small" value={moduleKeys}
                        onChange={(e) => setModuleKeys(e.target.value)}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selectedKeys) =>
                                selectedKeys.length === ALL_MODULE_KEYS.length
                                    ? "All Categories"
                                    : REVIEWABLE_MODULES.filter((m) => selectedKeys.includes(m.key)).map((m) => m.label).join(", "),
                        }}
                        sx={{ minWidth: 260 }}
                    >
                        {REVIEWABLE_MODULES.map((m) => (
                            <MenuItem key={m.key} value={m.key}>
                                <Checkbox checked={moduleKeys.includes(m.key)} size="small" sx={{ p: 0, mr: 1 }} />
                                <ListItemText primary={m.label} />
                            </MenuItem>
                        ))}
                    </TextField>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        {STATUS_TABS.map((tab) => (
                            <Button
                                key={tab.id} size="small" onClick={() => { setStatusFilter(tab.id); clearSelection(); }}
                                sx={{
                                    textTransform: "none", fontWeight: 700, borderRadius: "8px", px: 1.75,
                                    backgroundColor: statusFilter === tab.id ? "#2563eb" : "#f1f5f9",
                                    color: statusFilter === tab.id ? "#fff" : "#475569",
                                    "&:hover": { backgroundColor: statusFilter === tab.id ? "#1d4ed8" : "#e2e8f0" },
                                }}
                            >
                                {tab.label}
                            </Button>
                        ))}
                    </Box>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    <Chip label={`${summary.total} total`} size="small" sx={{ backgroundColor: "#f1f5f9", color: "#475569", fontWeight: 600 }} />
                    <Chip label={`${summary.pending} pending`} size="small" sx={{ backgroundColor: STATUS_COLORS.pending.bg, color: STATUS_COLORS.pending.fg, fontWeight: 600 }} />
                    <Chip label={`${summary.verified} verified`} size="small" sx={{ backgroundColor: STATUS_COLORS.verified.bg, color: STATUS_COLORS.verified.fg, fontWeight: 600 }} />
                    <Chip label={`${summary.rejected} rejected`} size="small" sx={{ backgroundColor: STATUS_COLORS.rejected.bg, color: STATUS_COLORS.rejected.fg, fontWeight: 600 }} />
                    <Chip label={`${summary.merged} merged as duplicates`} size="small" sx={{ backgroundColor: STATUS_COLORS.merged.bg, color: STATUS_COLORS.merged.fg, fontWeight: 600 }} />
                </Box>
            </Paper>

            <Box sx={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 3, alignItems: "start" }}>
                <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress size={22} /></Box>
                    ) : visibleReports.length === 0 ? (
                        <Box sx={{ p: 3, color: "#94a3b8", fontSize: "14px" }}>Nothing here.</Box>
                    ) : (
                        visibleReports.map((report) => {
                            const rMod = moduleByKey(report._moduleKey);
                            const uid = reportUid(report._moduleKey, report.id);
                            const loc = resolveLocationParts(rMod, report, locationNames);
                            return (
                                <Box
                                    key={uid} onClick={() => selectReport(report._moduleKey, report.id)}
                                    sx={{
                                        p: 2, borderBottom: "1px solid #e2e8f0", cursor: "pointer",
                                        backgroundColor: selectedUid === uid ? "#eff6ff" : "transparent",
                                        "&:hover": { backgroundColor: "#f8fafc" },
                                    }}
                                >
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: "13.5px" }}>
                                            #{report.id} — {loc.suco || "Unknown suco"}
                                        </Typography>
                                        {statusFilter === "all" && (
                                            <Chip
                                                label={report.verification_status} size="small"
                                                sx={{
                                                    height: "18px", fontSize: "10.5px", fontWeight: 700, textTransform: "capitalize",
                                                    backgroundColor: (STATUS_COLORS[report.verification_status] || STATUS_COLORS.pending).bg,
                                                    color: (STATUS_COLORS[report.verification_status] || STATUS_COLORS.pending).fg,
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Typography sx={{ fontSize: "12px", color: "#64748b" }}>
                                        {rMod.label} · {loc.municipality} · {report[rMod.dateField] || ""}
                                    </Typography>
                                    {report.report_group_count > 1 && (
                                        <Typography sx={{ fontSize: "11.5px", color: "#2563eb", fontWeight: 600, mt: 0.5 }}>
                                            1 active + {report.report_group_count - 1} merged
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })
                    )}
                </Paper>

                <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #e2e8f0", minHeight: 200 }}>
                    {!selectedUid ? (
                        <Typography sx={{ color: "#94a3b8", fontSize: "14px" }}>Select a report to review it.</Typography>
                    ) : !selected || !selectedMod ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress size={22} /></Box>
                    ) : (
                        <>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 0.5 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: "16px" }}>
                                    {selectedMod.label} — {[selectedLoc.municipality, selectedLoc.subMunicipality, selectedLoc.suco].filter(Boolean).join(", ")}
                                </Typography>
                                <Chip
                                    label={selected.verification_status} size="small"
                                    sx={{
                                        fontWeight: 700, textTransform: "capitalize",
                                        backgroundColor: (STATUS_COLORS[selected.verification_status] || STATUS_COLORS.pending).bg,
                                        color: (STATUS_COLORS[selected.verification_status] || STATUS_COLORS.pending).fg,
                                    }}
                                />
                            </Box>
                            <Typography sx={{ fontSize: "13px", color: "#64748b", mb: 2 }}>
                                Reported {selected[selectedMod.dateField] || "—"} by{" "}
                                {selected.submitted_by_detail?.full_name || "unknown user"}
                                {selected.submitted_by_detail?.position ? ` (${selected.submitted_by_detail.position})` : ""}
                                {selected.submitted_by_detail?.contact_number ? ` · ${selected.submitted_by_detail.contact_number}` : ""}
                                {selected.verified_by_detail?.full_name ? ` · reviewed by ${selected.verified_by_detail.full_name}` : ""}
                            </Typography>
                            {selectedMod.descField && selected[selectedMod.descField] && (
                                <Typography sx={{ fontSize: "13.5px", mb: 2 }}>{selected[selectedMod.descField]}</Typography>
                            )}
                            {selected.location && (
                                <Typography sx={{ fontSize: "12.5px", color: "#64748b", mb: 1 }}>{selected.location}</Typography>
                            )}
                            {(selected.latitude || selected.longitude) && (
                                <Typography sx={{ fontSize: "12.5px", color: "#64748b", mb: 1 }}>
                                    Coordinates: {selected.latitude ?? "—"}, {selected.longitude ?? "—"}
                                </Typography>
                            )}
                            {(selected.starting_point_lat || selected.ending_point_lat) && (
                                <Typography sx={{ fontSize: "12.5px", color: "#64748b", mb: 1 }}>
                                    Line: ({selected.starting_point_lat ?? "—"}, {selected.starting_point_long ?? "—"}) → ({selected.ending_point_lat ?? "—"}, {selected.ending_point_long ?? "—"})
                                </Typography>
                            )}
                            {selected.geojson_feature && (
                                <Typography sx={{ fontSize: "12.5px", color: "#64748b", mb: 1 }}>An area/polygon was drawn for this report.</Typography>
                            )}

                            {selected.image_url && (
                                <Box sx={{ mb: 2 }}>
                                    {imageBlobUrl ? (
                                        <a href={imageBlobUrl} target="_blank" rel="noreferrer">
                                            <img
                                                src={imageBlobUrl}
                                                alt="Attachment for this report"
                                                style={{ maxWidth: "100%", maxHeight: 280, borderRadius: 8, border: "1px solid #e2e8f0" }}
                                            />
                                        </a>
                                    ) : (
                                        <Typography sx={{ fontSize: "12.5px", color: "#94a3b8" }}>Loading photo…</Typography>
                                    )}
                                </Box>
                            )}

                            {otherDetails(selected, selectedMod).length > 0 && (
                                <>
                                    <Typography sx={{ fontWeight: 700, fontSize: "12px", color: "#64748b", textTransform: "uppercase", mb: 1 }}>
                                        {selectedMod.label} Details
                                    </Typography>
                                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "4px 16px", mb: 2 }}>
                                        {otherDetails(selected, selectedMod).map((d) => (
                                            <Typography key={d.key} sx={{ fontSize: "12.5px", color: "#374151" }}>
                                                <span style={{ color: "#94a3b8" }}>{d.label}:</span> {d.value}
                                            </Typography>
                                        ))}
                                    </Box>
                                </>
                            )}

                            {lossDamageDetails(selected).length > 0 && (
                                <>
                                    <Typography sx={{ fontWeight: 700, fontSize: "12px", color: "#64748b", textTransform: "uppercase", mb: 1 }}>
                                        Losses &amp; Damage
                                    </Typography>
                                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "4px 16px", mb: 2 }}>
                                        {lossDamageDetails(selected).map((d) => (
                                            <Typography key={d.key} sx={{ fontSize: "12.5px", color: "#374151" }}>
                                                <span style={{ color: "#94a3b8" }}>{d.label}:</span> {d.value}
                                            </Typography>
                                        ))}
                                    </Box>
                                </>
                            )}

                            {selectedMod.hasAdvisory && selected.verification_status === "pending" && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: "12px", color: "#64748b", textTransform: "uppercase", mb: 1 }}>
                                        Advisory or Impact (optional — your call as the reviewer, not the reporter's)
                                    </Typography>
                                    <TextField
                                        select size="small" value={advisoryChoice} onChange={(e) => setAdvisoryChoice(e.target.value)}
                                        sx={{ minWidth: 220 }}
                                    >
                                        <MenuItem value=""><em style={{ color: "#94a3b8" }}>No advisory</em></MenuItem>
                                        {ADVISORY_OPTIONS.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                    </TextField>
                                </Box>
                            )}
                            {selectedMod.hasAdvisory && selected.verification_status !== "pending" && selected.advisory && (
                                <Chip
                                    label={`Advisory: ${selected.advisory}`} size="small"
                                    sx={{ mb: 2, backgroundColor: "#eef2ff", color: "#4338ca", fontWeight: 700 }}
                                />
                            )}

                            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                                {selected.verification_status === "pending" ? (
                                    <>
                                        <Button
                                            variant="contained" disabled={busy} onClick={() => handleReview("verify")}
                                            sx={{ backgroundColor: "#3d7a4c", textTransform: "none", fontWeight: 700, "&:hover": { backgroundColor: "#2f6039" } }}
                                        >
                                            Verify
                                        </Button>
                                        <Button
                                            variant="outlined" disabled={busy} onClick={() => handleReview("reject")}
                                            sx={{ borderColor: "#ad4141", color: "#ad4141", textTransform: "none", fontWeight: 700, "&:hover": { borderColor: "#8f3333", backgroundColor: "#fdf2f2" } }}
                                        >
                                            Reject
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        variant="outlined" disabled={busy} onClick={() => handleReview("reopen")}
                                        sx={{ borderColor: "#2563eb", color: "#2563eb", textTransform: "none", fontWeight: 700, "&:hover": { borderColor: "#1d4ed8", backgroundColor: "#eff6ff" } }}
                                    >
                                        Reopen for Review
                                    </Button>
                                )}
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            <Typography sx={{ fontWeight: 700, fontSize: "12px", color: "#64748b", textTransform: "uppercase", mb: 1 }}>
                                This incident — 1 active{groupMembers.length > 0 ? ` + ${groupMembers.length} merged` : ""}
                            </Typography>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: "1px solid #f1f5f9" }}>
                                <Typography sx={{ fontSize: "13px" }}>
                                    #{selected.id} · {selected[selectedMod.dateField] || ""} · {selected.submitted_by_detail?.full_name || "unknown"}
                                </Typography>
                                <Chip label="Active" size="small" sx={{ height: "20px", fontSize: "11px", fontWeight: 700, backgroundColor: "#eff6ff", color: "#2563eb" }} />
                            </Box>

                            {groupMembers.length > 0 && (
                                <>
                                    <Typography sx={{ fontWeight: 700, fontSize: "12px", color: "#64748b", textTransform: "uppercase", mt: 3, mb: 1 }}>
                                        Merged into this report
                                    </Typography>
                                    <Typography sx={{ fontSize: "12px", color: "#94a3b8", mb: 1 }}>
                                        Expand a row for its full details. If one of these was actually the better/more
                                        complete report, make it active instead — it takes over as the group's face
                                        and this one folds under it.
                                    </Typography>
                                    {groupMembers.map((m) => {
                                        const isExpanded = expandedMemberIds.has(m.id);
                                        const detail = memberDetails[m.id];
                                        return (
                                            <Box key={m.id} sx={{ borderBottom: "1px solid #f1f5f9" }}>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
                                                    <Typography sx={{ fontSize: "13px" }}>
                                                        #{m.id} · {m[selectedMod.dateField] || ""} · {m.submitted_by_detail?.full_name || "unknown"}
                                                    </Typography>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                        <Button size="small" disabled={busy} onClick={() => handlePromote(m.id)} sx={{ textTransform: "none" }}>
                                                            Make Active
                                                        </Button>
                                                        <Button size="small" disabled={busy} onClick={() => handleUnmerge(m.id)} sx={{ textTransform: "none", color: "#ad4141" }}>
                                                            Unmerge
                                                        </Button>
                                                        <IconButton size="small" onClick={() => toggleMemberExpand(m.id)} aria-label={isExpanded ? "Hide full details" : "View full details"}>
                                                            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                                {isExpanded && (
                                                    detail
                                                        ? <MergedMemberDetail report={detail} mod={selectedMod} locationNames={locationNames} />
                                                        : <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={18} /></Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </>
                            )}

                            <Typography sx={{ fontWeight: 700, fontSize: "12px", color: "#64748b", textTransform: "uppercase", mt: 3, mb: 1 }}>
                                Possible duplicates — same suco, close in time
                            </Typography>
                            {candidates.length === 0 ? (
                                <Typography sx={{ fontSize: "13px", color: "#94a3b8" }}>No other reports for this hazard in this suco.</Typography>
                            ) : (
                                candidates.map((c) => (
                                    <Box key={c.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: "1px solid #f1f5f9" }}>
                                        <Typography sx={{ fontSize: "13px" }}>
                                            #{c.id} · {c[selectedMod.dateField] || ""} · {c.verification_status} · {c.submitted_by_detail?.full_name || "unknown"}
                                            {c.report_group_count > 1 ? ` · ${c.report_group_count} reports` : ""}
                                        </Typography>
                                        {c.merged_into ? (
                                            <Button size="small" disabled={busy} onClick={() => handleUnmerge(c.id)} sx={{ textTransform: "none", color: "#ad4141" }}>
                                                Unmerge
                                            </Button>
                                        ) : (
                                            <Button size="small" disabled={busy} onClick={() => handleMerge(c.id)} sx={{ textTransform: "none" }}>
                                                Merge into this
                                            </Button>
                                        )}
                                    </Box>
                                ))
                            )}
                        </>
                    )}
                </Paper>
            </Box>

            {snackbar.open && (
                <Box sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1400 }}>
                    <Alert onClose={() => setSnackbar((p) => ({ ...p, open: false }))} severity={snackbar.severity} variant="filled" sx={{ minWidth: "300px" }}>
                        {snackbar.message}
                    </Alert>
                </Box>
            )}
        </Box>
    );
};

export default ReportApprovals;
